import {
  Button,
  ButtonGroup,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import ImageUploader from "../../../../../components/utilities/ImageUploader";
import { BCPCircleCheck } from "../../../../../components/icons/bcp/BCPIcons";
import GenericPicker from "../../../../../components/utilities/GenericPicker";
import LocationPicker from "../../../../../components/utilities/LocationPicker";
import DateTimePickerComponent from "../../../../../components/utilities/DateTimePickerComponent";
import MarkupTextEditor from "../../../../../components/utilities/MarkupTextEditor";
import HelpButton from "../../../../../components/utilities/HelpButton";
import ReoccurringEventModal from "../../SubComponents/ReoccurringEventModal";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";

const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreationEventDetails({
  getInitialValues,
  getDetailsValidationSchema,
  submitDetails,
  setEventValues,
  eventValues,
  event,
  copyEvent,
  readOnly,
  gameStore,
  globalVars,
  authContext,
  eventSubType,
  gameSystem,
  locations,
  setLocations,
  setTimeZone,
  reoccurringOpen,
  setReoccurringOpen,
  eventId,
  EventStepper,
  step,
  setStep,
  circuits,
}) {
  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={getDetailsValidationSchema()}
        onSubmit={submitDetails}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          setFieldTouched,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Event Creation Progress Stepper */}
            <EventStepper
              step={step}
              setStep={setStep}
              event={event}
              circuits={circuits}
            />

            <ConnectedFocusError />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  onBlur={() => {
                    setEventValues(
                      eventValues
                        ? { ...eventValues, ...values }
                        : { ...values }
                    );
                  }}
                >
                  <Grid item xs={12}>
                    <ImageUploader
                      values={values}
                      label={values.name}
                      name="eventImage"
                      setFieldValue={setFieldValue}
                      fileId={event?.photoFileId || copyEvent?.photoFileId}
                      size="large"
                      defaultUrl={
                        event?.photo?.url ||
                        event?.photoUrl ||
                        copyEvent?.photo?.url ||
                        copyEvent?.photoUrl
                      }
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label="Event Name"
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      required
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      disabled={readOnly}
                      autoComplete="off"
                    />
                  </Grid>
                  {Boolean(
                    event?.gameStore?.id ||
                      copyEvent?.gameStore?.id ||
                      gameStore?.id
                  ) && (
                    <Grid item xs={12}>
                      <TextField
                        name="gameStore"
                        label="Organization"
                        value={
                          event?.gameStore?.name ||
                          copyEvent?.gameStore?.name ||
                          gameStore?.name ||
                          ""
                        }
                        variant="outlined"
                        fullWidth
                        disabled
                      />
                    </Grid>
                  )}
                  {!IS_UY && (
                    <Grid item xs={12}>
                      <ButtonGroup fullWidth>
                        <Button
                          variant={
                            values?.isOnlineEvent ? "outlined" : "contained"
                          }
                          onClick={() => {
                            setFieldValue("isOnlineEvent", false);
                          }}
                          startIcon={
                            !values?.isOnlineEvent && <BCPCircleCheck />
                          }
                        >
                          In-Person Event
                        </Button>
                        <Button
                          variant={
                            values?.isOnlineEvent ? "contained" : "outlined"
                          }
                          onClick={() => {
                            setFieldValue("isOnlineEvent", true);
                          }}
                          startIcon={
                            values?.isOnlineEvent && <BCPCircleCheck />
                          }
                        >
                          Virtual Event
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  )}
                  {globalVars?.eventSubTypes?.list?.length > 0 && (
                    <Grid item xs={12}>
                      {authContext?.authState?.identity?.isAdmin === true ||
                      authContext?.authState?.identity?.adminGameSystems?.includes(
                        event?.gameSystemId
                      ) ? (
                        <GenericPicker
                          name="eventSubType"
                          label={
                            globalVars?.eventSubTypes?.label || "Event Sub Type"
                          }
                          value={values.eventSubType}
                          handleChange={handleChange}
                          list={
                            globalVars?.eventSubTypes?.list
                          }
                          inclAny={false}
                        />
                      ) : (
                        <TextField
                          name="eventSubType"
                          label={
                            globalVars?.eventSubTypes?.label || "Event Sub Type"
                          }
                          value={event?.eventSubType || eventSubType || ""}
                          variant="outlined"
                          fullWidth
                          disabled
                        />
                      )}
                    </Grid>
                  )}
                  {(event?.gameSystem?.eventFormats?.list?.length > 0 ||
                    copyEvent?.gameSystem?.eventFormats?.list?.length > 0 ||
                    gameSystem?.eventFormats?.list?.length > 0) && (
                    <Grid item xs={12}>
                      <GenericPicker
                        name="eventFormat"
                        label={
                          event?.gameSystem?.eventFormats?.label ||
                          copyEvent?.gameSystem?.eventFormats?.label ||
                          gameSystem?.eventFormats?.label ||
                          "Format"
                        }
                        value={values.eventFormat}
                        handleChange={handleChange}
                        list={
                          event?.gameSystem?.eventFormats?.list ||
                          copyEvent?.gameSystem?.eventFormats?.list ||
                          gameSystem?.eventFormats?.list ||
                          []
                        }
                        inclAny={false}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <LocationPicker
                  locationId={values.locationId}
                  required
                  label={
                    values?.isOnlineEvent ? "Time Zone Location" : "Location"
                  }
                  locations={locations}
                  setLocations={setLocations}
                  setLocation={(location) => {
                    setFieldValue("location", location);
                    setFieldValue("locationId", location?.id);
                    setTimeZone(location?.timeZone);
                    var newLocations = [...locations];
                    var found = false;
                    newLocations.forEach((tempLocation) => {
                      if (tempLocation.id === location?.id) {
                        found = true;
                        tempLocation = {
                          ...tempLocation,
                          ...location,
                        };
                        return true;
                      }
                    });
                    if (!found) {
                      newLocations.push(location);
                    }
                    setLocations(newLocations);
                    setEventValues(
                      eventValues
                        ? { ...eventValues, ...values }
                        : { ...values }
                    );
                  }}
                  error={Boolean(touched.locationId && errors.locationId)}
                  helperText={touched.locationId && errors.locationId}
                  setFieldTouched={() => setFieldTouched("locationId")}
                  disabled={Boolean(readOnly)}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  onBlur={() => {
                    setEventValues(
                      eventValues
                        ? { ...eventValues, ...values }
                        : { ...values }
                    );
                  }}
                >
                  {values?.isOnlineEvent && (
                    <Grid item xs={12}>
                      <TextField
                        name="onlineEventLink"
                        label="Virtual Event Link (optional)"
                        value={values?.onlineEventLink}
                        variant="outlined"
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.values?.onlineEventLink &&
                            errors.values?.onlineEventLink
                        )}
                        helperText={
                          touched.values?.onlineEventLink &&
                          errors.values?.onlineEventLink
                        }
                        disabled={readOnly}
                        autoComplete="off"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <DateTimePickerComponent
                      name="eventDate"
                      label="Start Date/Time"
                      variant="outlined"
                      fullWidth
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={Boolean(touched.eventDate && errors.eventDate)}
                      helperText={touched.eventDate && errors.eventDate}
                      disabled={readOnly}
                      value={values.eventDate}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DateTimePickerComponent
                      name="eventEndDate"
                      label="End Date/Time"
                      fullWidth
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={Boolean(
                        touched.eventEndDate && errors.eventEndDate
                      )}
                      helperText={touched.eventEndDate && errors.eventEndDate}
                      disabled={readOnly}
                      value={values.eventEndDate}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MarkupTextEditor
                      name="eventDescriptionMarkup"
                      label="Event Description"
                      value={
                        values.eventDescriptionMarkup || values.eventDescription
                      }
                      onBlur={handleBlur}
                      onChange={({ text }) => {
                        setFieldValue("eventDescriptionMarkup", text);
                      }}
                      error={Boolean(
                        touched.eventDescriptionMarkup &&
                          errors.eventDescriptionMarkup
                      )}
                      helperText={
                        touched.eventDescriptionMarkup &&
                        errors.eventDescriptionMarkup
                      }
                      sx={{ height: "300px" }}
                      disabled={readOnly}
                    />
                  </Grid>

                  {!IS_UY && (
                    <Grid item xs={12}>
                      <TextField
                        name="externalUrl"
                        label="External URL (optional)"
                        value={values.externalUrl}
                        variant="outlined"
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.externalUrl && errors.externalUrl
                        )}
                        helperText={touched.externalUrl && errors.externalUrl}
                        disabled={readOnly}
                      />
                    </Grid>
                  )}

                  {authContext?.authState?.identity?.isAdmin && (
                    <>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            mt: 1,
                            mb: 1,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="700">
                          Admin Settings
                        </Typography>
                      </Grid>

                      {!IS_UY && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                League Event{" "}
                                <HelpButton title="League events do not use traditional pairings generation. When generating or setting the round number, pairings are not automatically generated. A Tournament Organizer or in some cases the Players set the pairings individually." />
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.leagueEvent}
                                onChange={(e) => {
                                  setFieldValue(
                                    "leagueEvent",
                                    !values.leagueEvent
                                  );
                                }}
                                disabled={readOnly}
                              />
                            }
                          />
                        </Grid>
                      )}

                      {/* reoccurringEvents only available on new events */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              Repeat Event{" "}
                              <HelpButton title="Enabling this will mark this event as a recurring event. You can modify the frequency and mark specific days that the events will occur." />
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.reoccurringEvent}
                              onChange={(e) => {
                                setFieldValue(
                                  "reoccurringEvent",
                                  !values.reoccurringEvent
                                );
                                setReoccurringOpen(!values.reoccurringEvent);
                              }}
                              disabled={!!eventId}
                            />
                          }
                        />
                      </Grid>

                      {values.reoccurringEvent && (
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setReoccurringOpen(true);
                            }}
                          >
                            Recurrence Details
                          </Button>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              Sponsored Event{" "}
                              <HelpButton
                                title={`Enabling this will place the event in the Featured Events on the home page.${
                                  globalVars?.pricing?.subscriptions?.playerSub
                                    ? ` In addition the event's ${
                                        event?.gameSystem?.listLabel ||
                                        copyEvent?.gameSystem?.listLabel ||
                                        gameSystem?.listLabel ||
                                        "List"
                                      }s will be viewable to non subscribers`
                                    : ""
                                }`}
                              />
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.sponsored}
                              onChange={(e) => {
                                let newSponsored = !values.sponsored;
                                setFieldValue("sponsored", newSponsored);
                                if (newSponsored) {
                                  setFieldValue("featured", newSponsored);
                                }
                              }}
                              disabled={readOnly}
                            />
                          }
                        />
                      </Grid>

                      {/* Only loads if reoccurringEvent is checked */}
                      <ReoccurringEventModal
                        values={values}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        reoccurringOpen={reoccurringOpen}
                        setReoccurringOpen={setReoccurringOpen}
                      />
                    </>
                  )}

                  {(authContext?.authState?.identity?.isAdmin === true ||
                    authContext?.authState?.identity?.adminGameSystems?.includes(
                      event?.gameSystemId
                    )) && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={
                          <Typography className="MuiFormControlLabel-label">
                            Featured Event{" "}
                            <HelpButton
                              title={`Enabling this will place the event in the Featured Events on the home page.`}
                            />
                          </Typography>
                        }
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)",
                          },
                        }}
                        control={
                          <Switch
                            color="primary"
                            checked={values.featured}
                            onChange={(e) => {
                              setFieldValue("featured", !values.featured);
                            }}
                            disabled={readOnly}
                          />
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Button fullWidth variant="contained" type="submit">
                  {eventId ? "Save Changes" : "Next: Game Configuration"}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Grid>
  );
}
